import type { JobPresentation } from '../../../job-schema.js';

/**
 * The outcomes each automatic payments card presents, by job name. A plain module (no Lit) so the unit tests check every
 * card against its job schema definition: jobSchema refuses a card whose outcomes differ, and the card then never opens.
 * Every outcome is hidden: the cards render their own buttons, and most outcomes are the server's.
 *
 * - setup: Propose answers before `proposed` is chosen.
 * - change-amounts: proposed is the server's, after propose-change answers; unchanged is Complete pressed twice with
 *   nothing changed.
 * - change-card: changed is the server's, after confirm-change-card holds the card.
 * - charge-failed: every outcome is the server's (serverOnly): the card's own buttons call card-action, which completes it.
 * - limit-reached: Close for the month calls card-action, which completes it; the sweep ends it month-ended.
 * - change-target: resolved is the server's: liquidity-check closes the card once the price recovers.
 * - raise-target: card-action completes the card (raise starts the change; cancel tells the people waiting the target
 *   won't be raised).
 * - resign-allowance: proposed is the server's: the resign lambda completes the card.
 * - stop: proposed is the server's: propose-stop completes the card.
 * - stopped (#40, on charge-failed, limit-reached, change-target, raise-target and resign-allowance): the server's:
 *   stop-finish closes every such open card once a stop is signed.
 */
type Outcomes = JobPresentation['outcomes'];

const hidden = (...names: string[]): Outcomes => Object.fromEntries(names.map((name) => [name, { type: 'hidden' }]));

export const AUTOPAY_CARD_OUTCOMES = {
  '@ns/public-ledger-auto-payments-setup': hidden('proposed', 'cancel'),
  '@ns/public-ledger-auto-payments-change-amounts': hidden('proposed', 'cancel', 'unchanged'),
  '@ns/public-ledger-auto-payments-change-card': hidden('changed', 'cancel'),
  '@ns/public-ledger-auto-payments-charge-failed': hidden('retry', 'confirmed', 'paid', 'change-card', 'stopped'),
  '@ns/public-ledger-auto-payments-limit-reached': hidden('close-for-month', 'month-ended', 'raise', 'stopped'),
  '@ns/public-ledger-auto-payments-change-target': hidden('resolved', 'change', 'stopped'),
  '@ns/public-ledger-auto-payments-raise-target': hidden('raise', 'cancel', 'stopped'),
  '@ns/public-ledger-auto-payments-resign-allowance': hidden('proposed', 'stopped'),
  '@ns/public-ledger-auto-payments-stop': hidden('proposed', 'cancel'),
} satisfies Record<string, Outcomes>;
